import {Injectable, computed, signal} from '@angular/core';
import {TrendGranularity} from '../../dashboard-analytics/models/dashboard-statistics.model';

export type PeriodMode = 'TODAY' | 'WEEK' | 'MONTH' | 'CUSTOM';

interface DateRange {
  start: Date;
  end: Date;
}

@Injectable({providedIn: 'root'})
export class DashboardPeriodService {

  readonly mode = signal<PeriodMode>('TODAY');
  readonly trendGranularity = signal<TrendGranularity>('DAY');

  // Custom range state — only meaningful when mode === 'CUSTOM'
  readonly customStart = signal<Date>(this.startOfDay(new Date()));
  readonly customEnd = signal<Date>(this.endOfDay(new Date()));

  // Current period range (depends on mode)
  readonly currentRange = computed<DateRange>(() => {
    const m = this.mode();
    if (m === 'CUSTOM') {
      return {start: this.customStart(), end: this.customEnd()};
    }
    return this.computePresetRange(m, 0);
  });

  readonly currentStart = computed(() => this.formatApi(this.currentRange().start));
  readonly currentEnd = computed(() => this.formatApi(this.currentRange().end));

  // Previous period — null when in custom range mode
  readonly previousRange = computed<DateRange | null>(() => {
    const m = this.mode();
    if (m === 'CUSTOM') return null;
    return this.computePresetRange(m, 1);
  });

  readonly previousStart = computed(() => {
    const r = this.previousRange();
    return r ? this.formatApi(r.start) : null;
  });

  readonly previousEnd = computed(() => {
    const r = this.previousRange();
    return r ? this.formatApi(r.end) : null;
  });

  readonly hasPreviousPeriod = computed(() => this.previousRange() !== null);

  // ── Mutators ────────────────────────────────────────────────

  setMode(m: PeriodMode): void {
    this.mode.set(m);
  }

  setCustomRange(start: Date, end: Date): void {
    this.customStart.set(this.startOfDay(start));
    this.customEnd.set(this.endOfDay(end));
    this.mode.set('CUSTOM');
  }

  /**
   * Formatted start/end for a preset mode, computed without touching the shared
   * period state. Lets a card derive its own Day/Week/Month range independently
   * of the global selector.
   */
  presetRange(mode: Exclude<PeriodMode, 'CUSTOM'>): {start: string; end: string} {
    const range = this.computePresetRange(mode, 0);
    return {start: this.formatApi(range.start), end: this.formatApi(range.end)};
  }

  /** The Pure Original trend and uploaded drill-down share this exact active range. */
  pureOriginalRange(): {start: string; end: string} {
    return this.mode() === 'TODAY'
      ? this.todayUntilNow()
      : {start: this.currentStart(), end: this.currentEnd()};
  }

  /** The Master trend and uploaded drill-down share this exact active range. */
  masterRange(): {start: string; end: string} {
    return this.pureOriginalRange();
  }

  /** Local calendar day so the Imports drill-down matches today's KPI. */
  todayUntilNow(): {start: string; end: string} {
    const now = new Date();
    return {start: this.formatApi(this.startOfDay(now)), end: this.formatApi(now)};
  }

  // ── Internal helpers ────────────────────────────────────────

  private computePresetRange(mode: Exclude<PeriodMode, 'CUSTOM'>, stepsBack: number): DateRange {
    const now = new Date();
    if (mode === 'TODAY') {
      const day = new Date(now.getFullYear(), now.getMonth(), now.getDate() - stepsBack);
      return {start: this.startOfDay(day), end: this.endOfDay(day)};
    }
    if (mode === 'WEEK') {
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      // Monday-based week
      const dow = (today.getDay() + 6) % 7; // 0 = Mon
      const monday = new Date(today);
      monday.setDate(today.getDate() - dow - (7 * stepsBack));
      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);
      return {start: this.startOfDay(monday), end: this.endOfDay(sunday)};
    }
    // MONTH
    const monthStart = new Date(now.getFullYear(), now.getMonth() - stepsBack, 1);
    const monthEnd = new Date(now.getFullYear(), now.getMonth() - stepsBack + 1, 0);
    return {start: this.startOfDay(monthStart), end: this.endOfDay(monthEnd)};
  }

  private startOfDay(d: Date): Date {
    const out = new Date(d);
    out.setHours(0, 0, 0, 0);
    return out;
  }

  private endOfDay(d: Date): Date {
    const out = new Date(d);
    out.setHours(23, 59, 59, 999);
    return out;
  }

  private formatApi(d: Date): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  }
}
